import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import CreateSleepScreenModal from './CreateSleepScreenModal';
import SleepScreenCreatedModal from './SleepScreenCreatedModal';
import SleepScreenCard from './SleepScreenCard';
import {
  ListSleepScreens,
  GetFridgeSchemasTemplates,
  FindSchemasBySleepScreenId,
} from '../../libs/GraphQL';
import { isActiveSchema } from '../../utils/schemaLifecycle';
import { getActiveSchemaIdSet } from '../../utils/activeSchemaIds';
import { SYSTEM_DEFAULT_ORDER, SYSTEM_DEFAULT_SET } from './sleepScreenHelpers';
import styles from './SleepScreensLanding.module.css';

// Counts only active fridges (live, or launching and not yet open):
// GetSleepScreenAssignmentCounts is pre-aggregated on the backend over every
// schema row (including decommissioned fridges), so we recompute here from the
// schema templates joined against active locations.
// Base templates are excluded — the count reflects fridges, not masters.
//
// Each schema can assign a standard-slot screen (sleepScreenId) and a New Fridge
// Experience screen (newFridgeExperienceSleepScreenId); tally both so NFE entries
// get a count instead of reading as unused.
const countLiveAssignments = (schemas, activeSchemaIds) => {
  const map = new Map();
  const tally = (sleepScreenId) => {
    if (!sleepScreenId) return;
    map.set(sleepScreenId, (map.get(sleepScreenId) || 0) + 1);
  };
  (schemas || []).forEach((schema) => {
    if (
      !isActiveSchema(schema.schemaId, activeSchemaIds, {
        includeBaseSchemas: false,
      })
    ) {
      return;
    }
    tally(schema.sleepScreenId);
    tally(schema.newFridgeExperienceSleepScreenId);
  });
  return map;
};

// Happy-hour slot assignments aren't exposed by getFridgeSchemasTemplates, so
// they come from findSchemasBySleepScreenId (which is slot-aware), counted the
// same way: distinct active, non-base fridges.
const HAPPY_HOUR_SLEEP_SCREEN_IDS = ['amHappyHour', 'pmHappyHour'];

const countLiveSchemaRows = (rows, activeSchemaIds) => {
  const seen = new Set();
  (rows || []).forEach((row) => {
    const id = row.schemaId;
    if (
      id &&
      !seen.has(id) &&
      isActiveSchema(id, activeSchemaIds, { includeBaseSchemas: false })
    ) {
      seen.add(id);
    }
  });
  return seen.size;
};

const compareByCreatedAtDesc = (a, b) => {
  // Newest first; entries without createdAt sink to the bottom.
  if (!a.createdAt && !b.createdAt) return 0;
  if (!a.createdAt) return 1;
  if (!b.createdAt) return -1;
  return b.createdAt.localeCompare(a.createdAt);
};

const compareByName = (a, b) =>
  (a.displayName || a.sleepScreenId || '').localeCompare(
    b.displayName || b.sleepScreenId || '',
  );

const SleepScreensLanding = () => {
  const [entries, setEntries] = useState([]);
  const [assignmentCounts, setAssignmentCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdEntry, setCreatedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNotAssigned, setShowNotAssigned] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchEntries = async () => {
      setLoading(true);
      // allSettled never rejects — per-query failures come back as rejected
      // results handled below. The try/finally is for the post-fetch
      // processing + guaranteeing the loading state always clears.
      try {
        const [
          listResult,
          schemasResult,
          activeIdsResult,
          ...happyHourResults
        ] = await Promise.allSettled([
          ListSleepScreens(),
          GetFridgeSchemasTemplates({}),
          getActiveSchemaIdSet(),
          ...HAPPY_HOUR_SLEEP_SCREEN_IDS.map((id) =>
            FindSchemasBySleepScreenId(id),
          ),
        ]);
        if (cancelled) return;

        if (listResult.status === 'fulfilled') {
          setEntries(
            (listResult.value || []).slice().sort(compareByCreatedAtDesc),
          );
        } else {
          console.error('listSleepScreens failed:', listResult.reason);
          toast.error('Failed to load sleep screen library.');
        }

        if (
          schemasResult.status === 'fulfilled' &&
          activeIdsResult.status === 'fulfilled'
        ) {
          const activeSchemaIds = activeIdsResult.value;
          const counts = countLiveAssignments(
            schemasResult.value,
            activeSchemaIds,
          );
          HAPPY_HOUR_SLEEP_SCREEN_IDS.forEach((id, i) => {
            const result = happyHourResults[i];
            if (result && result.status === 'fulfilled') {
              const count = countLiveSchemaRows(result.value, activeSchemaIds);
              if (count > 0) counts.set(id, count);
            } else if (result && result.status === 'rejected') {
              console.error(
                `findSchemasBySleepScreenId(${id}) failed:`,
                result.reason,
              );
            }
          });
          setAssignmentCounts(counts);
        } else {
          if (schemasResult.status === 'rejected') {
            console.error(
              'getFridgeSchemasTemplates failed:',
              schemasResult.reason,
            );
          }
          if (activeIdsResult.status === 'rejected') {
            console.error(
              'getActiveSchemaIdSet failed:',
              activeIdsResult.reason,
            );
          }
          setAssignmentCounts(null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load sleep screen library:', err);
        toast.error('Failed to load sleep screen library.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEntries();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreated = (entry) => {
    setShowCreateModal(false);
    setCreatedEntry(entry);
    setEntries((prev) => [
      entry,
      ...prev.filter((e) => e.sleepScreenId !== entry.sleepScreenId),
    ]);
  };

  const countFor = (entry) => {
    if (!assignmentCounts) return null;
    if (!assignmentCounts.has(entry.sleepScreenId)) return null;
    return assignmentCounts.get(entry.sleepScreenId);
  };

  const filteredEntries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return entries;
    return entries
      .filter((entry) => {
        const name = (entry.displayName || '').toLowerCase();
        const id = (entry.sleepScreenId || '').toLowerCase();
        return name.includes(term) || id.includes(term);
      })
      .sort(compareByName);
  }, [entries, searchTerm]);

  const buckets = useMemo(() => {
    const systemDefaults = [];
    const inUse = [];
    const notAssigned = [];
    if (!assignmentCounts) {
      return { systemDefaults, inUse, notAssigned };
    }
    entries.forEach((entry) => {
      if (SYSTEM_DEFAULT_SET.has(entry.sleepScreenId)) {
        systemDefaults.push(entry);
        return;
      }
      const count = assignmentCounts.get(entry.sleepScreenId) ?? 0;
      if (count === 0) notAssigned.push(entry);
      else inUse.push(entry);
    });
    systemDefaults.sort(
      (a, b) =>
        SYSTEM_DEFAULT_ORDER.indexOf(a.sleepScreenId) -
        SYSTEM_DEFAULT_ORDER.indexOf(b.sleepScreenId),
    );
    inUse.sort((a, b) => {
      const ca = assignmentCounts.get(a.sleepScreenId) ?? 0;
      const cb = assignmentCounts.get(b.sleepScreenId) ?? 0;
      if (cb !== ca) return cb - ca;
      return compareByCreatedAtDesc(a, b);
    });
    notAssigned.sort(compareByName);
    return { systemDefaults, inUse, notAssigned };
  }, [entries, assignmentCounts]);

  const hasSearch = searchTerm.trim().length > 0;
  const canBucket = assignmentCounts !== null;

  const renderCard = (entry) => (
    <SleepScreenCard
      key={entry.sleepScreenId}
      entry={entry}
      assignmentCount={countFor(entry)}
    />
  );

  const renderListBody = () => {
    if (loading) {
      return <div className={styles.stateBlock}>Loading…</div>;
    }
    if (entries.length === 0) {
      return (
        <div className={styles.stateBlock}>
          No sleep screens yet — click <strong>+ new entry</strong> to add the
          first one to the library.
        </div>
      );
    }
    if (hasSearch) {
      if (filteredEntries.length === 0) {
        return (
          <div className={styles.stateBlock}>
            No entries match &quot;{searchTerm.trim()}&quot;.
          </div>
        );
      }
      return (
        <div className={styles.grid}>{filteredEntries.map(renderCard)}</div>
      );
    }
    if (!canBucket) {
      // Counts query failed — fall back to a flat sorted grid.
      return <div className={styles.grid}>{entries.map(renderCard)}</div>;
    }
    return (
      <>
        {buckets.systemDefaults.length > 0 && (
          <section className={styles.bucket}>
            <div className={styles.bucketHeader}>
              <h2 className={styles.bucketTitle}>
                System Defaults
                <span className={styles.bucketCount}>
                  {buckets.systemDefaults.length}{' '}
                  {buckets.systemDefaults.length === 1 ? 'entry' : 'entries'}
                </span>
              </h2>
            </div>
            <div className={styles.grid}>
              {buckets.systemDefaults.map(renderCard)}
            </div>
          </section>
        )}
        {buckets.inUse.length > 0 && (
          <section className={styles.bucket}>
            <div className={styles.bucketHeader}>
              <h2 className={styles.bucketTitle}>
                In Use
                <span className={styles.bucketCount}>
                  {buckets.inUse.length}{' '}
                  {buckets.inUse.length === 1 ? 'entry' : 'entries'}
                </span>
              </h2>
            </div>
            <div className={styles.grid}>{buckets.inUse.map(renderCard)}</div>
          </section>
        )}
        {buckets.notAssigned.length > 0 && (
          <section className={styles.bucket}>
            <div className={styles.bucketHeader}>
              <button
                type="button"
                className={styles.bucketToggle}
                onClick={() => setShowNotAssigned((v) => !v)}
                aria-expanded={showNotAssigned}
              >
                <h2 className={styles.bucketTitle}>
                  Available
                  <span className={styles.bucketCount}>
                    {buckets.notAssigned.length}{' '}
                    {buckets.notAssigned.length === 1 ? 'entry' : 'entries'}
                  </span>
                </h2>
                <span className={styles.bucketCaret}>
                  {showNotAssigned ? '▼' : '▶'}
                </span>
              </button>
              <p className={styles.bucketHelper}>
                Not currently assigned to any fridges.
              </p>
            </div>
            {showNotAssigned && (
              <div className={styles.grid}>
                {buckets.notAssigned.map(renderCard)}
              </div>
            )}
          </section>
        )}
      </>
    );
  };

  return (
    <main className={styles.container}>
      <div className={styles.headerRow}>
        <h1>Sleep Screen Library</h1>
        <div className={styles.headerActions}>
          <Link to="/sleep-screens/assign" className={styles.assignButton}>
            assign fridges
          </Link>
          <button
            type="button"
            className={styles.createButton}
            onClick={() => setShowCreateModal(true)}
          >
            + new entry
          </button>
        </div>
      </div>

      <div className={styles.toolbar}>
        <span className={styles.entryCount}>
          {hasSearch
            ? `${filteredEntries.length} of ${entries.length} entries`
            : `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`}
        </span>
        <input
          type="search"
          className={styles.searchInput}
          placeholder="search by name or id…"
          aria-label="Search sleep screens by name or ID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {renderListBody()}

      <CreateSleepScreenModal
        show={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onCreated={handleCreated}
      />

      <SleepScreenCreatedModal
        entry={createdEntry}
        onClose={() => setCreatedEntry(null)}
      />
    </main>
  );
};

export default SleepScreensLanding;
